import PropTypes from 'prop-types';
import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { addPageData, navigateWizard } from '../../actions';
import Button from '../../components/Button';
import CombinedTextField from '../../components/CombinedTextField';
import InputField from '../../components/InputField';
import PageLayout from '../../components/PageLayout';
import Select from '../../components/Select';
import { MAX_AMOUNT_LIMIT, MIN_AMOUNT_LIMIT } from '../../constants/numberLimit';
import analytics from '../../lib/analytics';
import applyDefaultCurrencyOn from '../../lib/defaultCurrencyHelper';
import { isNumber } from '../../lib/helpers';
import useDefaultCurrency from '../../lib/hooks/useDefaultCurrency';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';
import { validCurrencyCodesSelector } from '../../reducers/selectors';
import { updateValue } from '../../utils/utils';

const repaymentMethods = [
  { label: 'How do you repay this?', value: '', isPlaceholder: true },
  { label: 'You clear your balance each month.', value: 'monthly' },
  { label: "You're slowly paying it off when you can.", value: 'slowly' },
  { label: "You're not able to pay it off yet.", value: 'not_yet' },
];

export const CreditCardDebtPage = ({
  dispatchWizardNav,
  dispatchAddPageData,
  creditCardDebt,
  validCurrencyCodes,
}) => {
  const [defaultCurrency, currencies] = useDefaultCurrency();
  const {
    currency: initialCurrency,
    amount: initialAmount,
    repaymentMethod: initialRepaymentMethod,
  } = applyDefaultCurrencyOn(creditCardDebt || {}, defaultCurrency);
  const [currency, setCurrency] = useState(initialCurrency);
  const [amount, setAmount] = useState(initialAmount);
  const [repaymentMethod, setRepaymentMethod] = useState(initialRepaymentMethod || '');
  const [error, setError] = useState(null);

  const changeCurrency = event => {
    event.preventDefault();
    setCurrency(event.target.value);
  };

  const changeRepaymentMethod = event => {
    event.preventDefault();
    setRepaymentMethod(event.target.value);
  };

  const isComplete = useMemo(
    () => isNumber(amount) && validCurrencyCodes.includes(currency) && repaymentMethod.length,
    [amount, currency, validCurrencyCodes, repaymentMethod],
  );

  const isInvalid = useMemo(() => {
    if (!isComplete) {
      return true;
    }
    const validLength = () => {
      const decimalIndex = amount.search(/\./);
      const splitAmount = decimalIndex > -1 ? amount.split('.') : amount.split(',');
      const numericValue = splitAmount[0];
      const decimalValue = splitAmount[1] || 0;
      if (numericValue.length <= 13 && decimalValue.toString().length <= 2) {
        return true;
      }
      return false;
    };

    const parsedAmount = parseFloat(amount);
    if (parsedAmount < 0 || !validLength()) {
      setError('Invalid amount');
      return true;
    }

    return false;
  }, [isComplete, amount]);

  const handleSubmit = event => {
    event.preventDefault();
    if (isInvalid) {
      return;
    }

    analytics.sendEvent('Click - Next', 'credit-card-debt');

    const payload = payloadFactory.createAddPageDataPayload('creditCardDebt', {
      amount,
      currency,
      repaymentMethod,
    });
    dispatchAddPageData(payload);
    dispatchWizardNav({ dir: 'fwd' });
  };

  const handleApplyDefault = () => {
    analytics.sendEvent("Click - Don't have outstanding credit?", 'credit-card-debt');

    const payload = payloadFactory.createAddPageDataPayload('creditCardDebt', {
      amount: null,
      currency: null,
      repaymentMethod: null,
    });
    dispatchAddPageData(payload);
    dispatchWizardNav({ dir: 'fwd' });
  };

  const inputErrorClass = isInvalid ? 'has-error' : '';

  return (
    <PageLayout
      title="What is your outstanding credit card debt?"
      helpText="Your credit card balance gives us a picture of your outstanding revolving credit. You
      should include store cards, credit cards and credit lines. We'll check this on
      this on your credit report when you finalise your loan."
    >
      <form className="row-spacer" onSubmit={handleSubmit}>
        <div className="row">
          <div className="col col--12/16-large">
            <CombinedTextField>
              <Select
                id="credit-card-debt-currency"
                value={currency}
                onChange={changeCurrency}
                options={currencies}
              />

              <InputField
                type="text"
                inputMode="numeric"
                pattern="\d*"
                id="credit-card-debt-amount"
                placeholder="Enter debt amount"
                value={amount}
                onChange={e => updateValue(e, setAmount, MAX_AMOUNT_LIMIT)}
                className={inputErrorClass}
                min={MIN_AMOUNT_LIMIT}
                max={MAX_AMOUNT_LIMIT}
              />
            </CombinedTextField>

            {isInvalid && <div className="row-spacer text-error">{error}</div>}
          </div>
        </div>

        <div className="row">
          <div className="col col--12/16-large">
            <Select
              id="credit-card-debt-repayment-method"
              value={repaymentMethod}
              onChange={changeRepaymentMethod}
              options={repaymentMethods}
            />
          </div>
        </div>
        <div className="row">
          <div className="col col--5/16-large">
            <Button type="submit" isDisabled={isInvalid}>
              Next
            </Button>
          </div>
        </div>
      </form>

      <div className="row-spacer">
        <Link to="#" onClick={handleApplyDefault}>
          Don&apos;t have any outstanding credit?
        </Link>
      </div>
    </PageLayout>
  );
};

CreditCardDebtPage.propTypes = {
  dispatchAddPageData: PropTypes.func.isRequired,
  dispatchWizardNav: PropTypes.func.isRequired,
  creditCardDebt: PropTypes.shape({
    amount: PropTypes.string,
    currency: PropTypes.string,
    repaymentMethod: PropTypes.string,
  }).isRequired,
  validCurrencyCodes: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export const mapStateToProps = state => ({
  creditCardDebt: state.builder.ui.creditCardDebt,
  validCurrencyCodes: validCurrencyCodesSelector(state),
});

export const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchWizardNav: navigateWizard,
};

export default connect(mapStateToProps, mapDispatchToProps)(CreditCardDebtPage);
